'use client';
/**
 * Briques communes aux écrans qui appellent l'assistance (lot GED-13).
 *
 * ── Pourquoi ce fichier existe ──────────────────────────────────────────────
 *
 * L'entretien de déclaration NC (`EntretienDeclarationNC`, Claude) se contente
 * de DISPARAÎTRE quand il n'est pas configuré : c'est tenable pour une aide
 * posée sur un formulaire qui fonctionne très bien sans elle. Ce ne l'est plus
 * pour les deux tâches câblées ici, qui ont leur propre encart : un bloc qui s'évapore laisse
 * croire à un droit manquant, ou à un écran cassé.
 *
 * Or l'état « pas de moteur » n'est PAS une avarie passagère. Le laboratoire est
 * en cours d'achat de ses serveurs : `IA_BASE_URL` restera vide pendant des
 * semaines. C'est donc l'état NOMINAL de ces deux écrans, celui qu'il faut
 * soigner en premier — il doit se lire calmement, sans rouge, sans bouton qui
 * tourne dans le vide, et sans rien écrire en console.
 *
 * ── Trois états, trois phrases ──────────────────────────────────────────────
 *
 * Ils ne se confondent pas, parce qu'ils n'appellent pas la même conduite :
 *  · ÉTEINT      — rien à faire, rien à signaler, l'application marche.
 *  · INJOIGNABLE — le serveur devrait répondre : c'est à signaler.
 *  · DÉLAI       — il a répondu trop tard : on réessaie sur moins de texte.
 *
 * ── Ce que ces briques ne font jamais ───────────────────────────────────────
 *
 * Elles n'écrivent rien. Aucun composant de ce fichier n'appelle `smq`, `act`
 * ni aucune route d'écriture : elles affichent une PROPOSITION. L'écriture, s'il
 * y en a une, est un geste explicite pris ailleurs, par l'écran appelant.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import type { EchecIA } from '@/lib/store';
import { I, MarqueIA } from '@/components/ui';

/* -------------------------------------------------------------------------- */
/* Disponibilité                                                              */
/* -------------------------------------------------------------------------- */

export type DisponibiliteIA =
  | { utilisable: true }
  | { utilisable: false; titre: string; detail: string; ton: 'neutre' | 'ambre' };

/**
 * Est-ce que CETTE tâche est appelable, ici et maintenant ?
 *
 * Deux conditions, dans cet ordre : le moteur répond, et le catalogue serveur
 * a retenu la tâche pour mon rôle (`cqi-lire` est réservée à
 * `rq` et `redacteur` — c'est le serveur qui filtre, on ne redéclare pas la
 * règle ici, on lit sa réponse).
 */
export function useDisponibiliteIA(tache: string): DisponibiliteIA {
  const { iaEtat, iaTaches } = useGed();
  if (iaEtat === 'attente') {
    return {
      utilisable: false, ton: 'neutre',
      titre: 'Vérification de l’assistance…',
      detail: 'L’application demande au serveur si un moteur d’assistance est disponible.',
    };
  }
  if (iaEtat === 'eteint') {
    return {
      utilisable: false, ton: 'neutre',
      titre: 'Assistance non installée sur ce serveur',
      detail: 'Aucun moteur d’assistance n’est configuré : le laboratoire n’a pas encore mis en service '
        + 'le serveur qui l’hébergera. Ce n’est pas une panne, il n’y a rien à réessayer, et tout le reste '
        + 'de l’écran fonctionne normalement.',
    };
  }
  if (iaEtat === 'injoignable') {
    return {
      utilisable: false, ton: 'ambre',
      titre: 'État de l’assistance inconnu',
      detail: 'Le serveur n’a pas répondu quand l’application lui a demandé si l’assistance était '
        + 'disponible. Rechargez l’écran ; si cela se reproduit, signalez-le à l’informatique.',
    };
  }
  if (!iaTaches.includes(tache)) {
    return {
      utilisable: false, ton: 'neutre',
      titre: 'Assistance réservée à d’autres rôles',
      detail: 'Cette aide est ouverte au responsable qualité et aux rédacteurs. Votre profil n’y donne '
        + 'pas accès — le reste de l’écran, lui, ne change pas.',
    };
  }
  return { utilisable: true };
}

/* -------------------------------------------------------------------------- */
/* Rendus                                                                     */
/* -------------------------------------------------------------------------- */

const encart = (ton: 'neutre' | 'ambre'): React.CSSProperties => ({
  display: 'grid', gap: 4, padding: '10px 13px', borderRadius: 'var(--radius-md)',
  background: ton === 'ambre' ? 'var(--amber-50, #FFF8EC)' : 'var(--ia-surface)',
  border: `1px solid ${ton === 'ambre' ? 'var(--amber-300, #EFC77B)' : 'var(--ia-bord)'}`,
});

/** L'encart qui remplace les commandes quand la tâche n'est pas appelable.
 *  Il n'est jamais rouge : ne pas avoir de moteur n'est pas une erreur. */
export function IndisponibiliteIA({ dispo }: { dispo: DisponibiliteIA }) {
  if (dispo.utilisable) return null;
  return (
    <div style={encart(dispo.ton)} role="note">
      <strong style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-strong)' }}>
        {dispo.ton === 'ambre' ? <I n="alert" s={13} /> : <MarqueIA s={13} />}{dispo.titre}
      </strong>
      <span style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{dispo.detail}</span>
    </div>
  );
}

/** Ce que dit chaque échec, et ce qu'il faut en faire. Table plutôt que suite de
 *  ternaires : c'est la LISTE des états distingués, et elle doit se lire d'un
 *  coup d'œil quand on se demande si l'écran les confond. */
const ECHECS: Record<EchecIA, { titre: string; conduite: string; ton: 'neutre' | 'ambre' }> = {
  eteint: {
    titre: 'Assistance non installée',
    conduite: 'Aucun moteur n’est configuré sur ce serveur. Rien à réessayer — poursuivez à la main.',
    ton: 'neutre',
  },
  panne: {
    titre: 'Le moteur d’assistance n’a pas répondu',
    conduite: 'Le moteur est configuré mais injoignable ou en erreur. Réessayez ; si cela se répète, '
      + 'signalez-le à l’informatique. Rien n’a été enregistré.',
    ton: 'ambre',
  },
  delai: {
    titre: 'Le moteur d’assistance a mis trop de temps',
    conduite: 'Il a été abandonné avant de répondre — ce n’est pas la même chose qu’une panne : le '
      + 'serveur est là, mais lent ou surchargé. Réessayez, ou soumettez moins de texte. Rien n’a été enregistré.',
    ton: 'ambre',
  },
  refus: {
    titre: 'Demande déclinée',
    conduite: 'L’assistance n’a pas voulu traiter ce texte. Reformulez, ou poursuivez à la main.',
    ton: 'neutre',
  },
  droit: {
    titre: 'Assistance non autorisée',
    conduite: 'Votre rôle ne donne pas accès à cette aide.',
    ton: 'neutre',
  },
};

/** Le compte rendu d'un appel qui n'a rien rendu. `message` est celui du moteur :
 *  il est repris tel quel, en second, parce qu'il porte parfois un détail utile
 *  (« texte trop long », « réponse coupée ») que la conduite générique n'a pas. */
export function EchecAssistanceIA({ echec }: { echec: { motif: EchecIA; message: string } }) {
  const e = ECHECS[echec.motif] ?? ECHECS.panne;
  return (
    <div style={encart(e.ton)} role="status">
      <strong style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-strong)' }}>
        <I n="alert" s={13} />{e.titre}
      </strong>
      <span style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{e.conduite}</span>
      {echec.message && (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          Message du serveur : {echec.message}
        </span>
      )}
    </div>
  );
}

/** Rappel porté par les DEUX écrans, au même endroit et dans les mêmes mots :
 *  une proposition n'est pas un enregistrement. */
export function RappelProposition({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
      <strong style={{ color: 'var(--text-body)' }}>Proposition — rien n’est enregistré.</strong> {children}
    </p>
  );
}
